// src/admin/pages/AdminReports.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, query, getDocs } from 'firebase/firestore';
import {
  LineChart, Line,
  PieChart, Pie,
  XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
  BarChart, Bar
} from 'recharts';
import AdminHeader from '../../components/AdminHeader';
import AdminSidebar from '../../components/AdminSidebar';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addArabicFont } from '../../assets/fonts/pdfArabicSupport';
import '../../styles/AdminReports.css';
import { useLanguage } from '../../contexts/LanguageContext';
import LoadingSpinner from '../../components/LoadingSpinner';

const AdminReports = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('week');
  const [reportType, setReportType] = useState('orders');
  const [chartData, setChartData] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [completedOrdersData, setCompletedOrdersData] = useState([]);
  const [completedByRestaurantData, setCompletedByRestaurantData] = useState([]);
  const [completedByDriverData, setCompletedByDriverData] = useState([]);
  const [showCompletedSection, setShowCompletedSection] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    totalRevenue: 0,
    totalDeliveryFees: 0,
    avgOrderValue: 0,
    totalRestaurants: 0,
    totalDrivers: 0,
    totalCustomers: 0,
    avgDeliveryTime: 0,
    totalCompletedRevenue: 0
  });

  const { t } = useLanguage();

  const formatDate = useCallback((date) =>
    date.toLocaleDateString('ar-EG', { day: 'numeric', month: 'short' }),
  [],);

  const getDayOfWeek = useCallback((date) =>
    date.toLocaleDateString('ar-EG', { weekday: 'short' }),
  [],);

  const getWeekNumber = useCallback((date) => {
    const first = new Date(date.getFullYear(), 0, 1);
    const diff = (date - first) / 86400000;
    return Math.ceil((diff + first.getDay() + 1) / 7);
  }, []);

  // Chart preparation functions
  const prepareChartData = useCallback((data) => {
    const map = {};
    if (dateRange === 'day') {
      for (let h = 0; h < 24; h++) map[`${h}:00`] = { time: `${h}:00`, orders: 0, revenue: 0 };
      data.forEach(o => {
        const key = `${o.date.getHours()}:00`;
        map[key].orders++;
        map[key].revenue += o.total;
      });
    } else {
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        map[formatDate(d)] = { time: formatDate(d), orders: 0, revenue: 0 };
      }
      data.forEach(o => {
        const key = o.dateFormatted;
        if (map[key]) {
          map[key].orders++;
          map[key].revenue += o.total;
        }
      });
    }
    setChartData(Object.values(map).map(item => ({
      time: item.time,
      orders: item.orders,
      revenue: parseFloat(item.revenue.toFixed(3))
    })));
  }, [dateRange, formatDate]);

  const preparePaymentMethodData = useCallback((data) => {
    const m = {};
    data.forEach(o => {
      m[o.paymentMethod] = (m[o.paymentMethod] || 0) + 1;
    });
    setPaymentMethodData(Object.entries(m).map(([name, value]) => ({ name, value })));
  }, []);

  const prepareOrderStatusData = useCallback((data) => {
    const s = {};
    data.forEach(o => {
      s[o.status] = (s[o.status] || 0) + 1;
    });
    setOrderStatusData(Object.entries(s).map(([name, value]) => ({ name, value })));
  }, []);

  const prepareCompletedOrdersData = useCallback((data) => {
    const completed = data.filter(o => o.status === 'completed');
    setCompletedOrdersData(completed);
    const byR = {}, byD = {};
    completed.forEach(o => {
      byR[o.restaurantName] = byR[o.restaurantName] || { name: o.restaurantName, count: 0, revenue: 0 };
      byR[o.restaurantName].count++;
      byR[o.restaurantName].revenue += o.total;
      byD[o.driverName] = byD[o.driverName] || { name: o.driverName, count: 0, revenue: 0 };
      byD[o.driverName].count++;
      byD[o.driverName].revenue += o.total;
    });
    setCompletedByRestaurantData(Object.values(byR).sort((a,b)=>b.count-a.count).slice(0,10));
    setCompletedByDriverData(Object.values(byD).sort((a,b)=>b.count-a.count).slice(0,10));
  }, []);

  const calculateStats = useCallback((data) => {
    const total = data.length;
    const completed = data.filter(o => o.status === 'completed').length;
    const cancelled = data.filter(o => o.status === 'cancelled').length;
    const revenue = data.reduce((s,o)=> s + (o.status === 'completed'?o.total:0), 0);
    const fees = data.reduce((s,o) => s + o.deliveryFee, 0);
    const avgVal = completed ? revenue / completed : 0;
    setStats({
      totalOrders: total,
      completedOrders: completed,
      cancelledOrders: cancelled,
      totalRevenue: revenue,
      totalDeliveryFees: fees,
      avgOrderValue: avgVal,
      totalRestaurants: new Set(data.map(o => o.restaurantId)).size,
      totalDrivers: new Set(data.map(o => o.driverId)).size,
      totalCustomers: new Set(data.map(o => o.customerId)).size,
      avgDeliveryTime: 0,
      totalCompletedRevenue: revenue
    });
  }, []);

  // Fetch and enhance orders
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'orders')));
      const raw = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const enhanced = raw.map(o => {
        const d = o.createdAt?.toDate?.() || new Date();
        return {
          ...o,
          date: d,
          dateFormatted: formatDate(d),
          dayOfWeek: getDayOfWeek(d),
          week: getWeekNumber(d),
          total: Number(o.total) || 0,
          deliveryFee: Number(o.deliveryFee) || 0,
          status: o.status || 'غير معروف',
          paymentMethod: o.paymentMethod || 'غير معروف',
          restaurantName: o.restaurantName || 'غير معروف',
          driverName: o.driverName || 'غير معروف',
          customerName: o.customerName || 'غير معروف'
        };
      });
      setOrders(enhanced);
    } catch (e) {
      console.error('خطأ في جلب البيانات:', e);
    } finally {
      setLoading(false);
    }
  }, [formatDate, getDayOfWeek, getWeekNumber]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // بعد جلب جميع الطلبات في orders:
  const completedOrders = orders.filter(o => o.status === 'completed');

  // Filter & prepare when orders or dateRange change
  useEffect(() => {
    if (!orders.length) return;
    const now = new Date();
    const filtered = orders.filter(o => {
      const d = o.date;
      switch (dateRange) {
        case 'day':
          return d.toDateString() === now.toDateString();
        case 'week':
          return d >= new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        case 'month':
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        case 'year':
          return d.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    });
    setFilteredOrders(filtered);
    prepareChartData(filtered);
    preparePaymentMethodData(filtered);
    prepareOrderStatusData(filtered);
    // استخدم completedOrders بدلاً من prepareCompletedOrdersData(filtered)
    // احسب أفضل المطاعم والسائقين من completedOrders فقط
    const byR = {}, byD = {};
    completedOrders.forEach(o => {
      byR[o.restaurantName] = byR[o.restaurantName] || { name: o.restaurantName, count: 0, revenue: 0 };
      byR[o.restaurantName].count++;
      byR[o.restaurantName].revenue += o.total;
      byD[o.driverName] = byD[o.driverName] || { name: o.driverName, count: 0, revenue: 0 };
      byD[o.driverName].count++;
      byD[o.driverName].revenue += o.total;
    });
    setCompletedByRestaurantData(Object.values(byR).sort((a,b)=>b.count-a.count).slice(0,10));
    setCompletedByDriverData(Object.values(byD).sort((a,b)=>b.count-a.count).slice(0,10));
    // احسب الإحصائيات من completedOrders فقط
    const total = filtered.length;
    const completed = completedOrders.length;
    const cancelled = filtered.filter(o => o.status === 'cancelled').length;
    const revenue = completedOrders.reduce((s,o)=> s + (o.total || 0), 0);
    const fees = filtered.reduce((s,o) => s + o.deliveryFee, 0);
    const avgVal = completed ? revenue / completed : 0;
    setStats({
      totalOrders: total,
      completedOrders: completed,
      cancelledOrders: cancelled,
      totalRevenue: revenue,
      totalDeliveryFees: fees,
      avgOrderValue: avgVal,
      totalRestaurants: new Set(filtered.map(o => o.restaurantId)).size,
      totalDrivers: new Set(filtered.map(o => o.driverId)).size,
      totalCustomers: new Set(filtered.map(o => o.customerId)).size,
      avgDeliveryTime: 0,
      totalCompletedRevenue: revenue
    });
  }, [orders, dateRange, prepareChartData, preparePaymentMethodData, prepareOrderStatusData]);

  // Excel export
  const exportToExcel = () => {
    const rows = filteredOrders.map(o => ({
      'رقم الطلب': o.id,
      'التاريخ': o.dateFormatted,
      'الحالة': o.status,
      'المبلغ': o.total.toFixed(3),
      'رسوم التوصيل': o.deliveryFee.toFixed(3),
      'طريقة الدفع': o.paymentMethod
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(XLSX.utils.book_new(), ws, 'Report');
    XLSX.writeFile(XLSX.utils.book_new(), `تقرير_${reportType}_${dateRange}.xlsx`);
  };

  // PDF export with Arabic support
  const exportToPDF = () => {
  const lang = localStorage.getItem('lang') || 'ar';

  const isArabic = lang === 'ar';

  const title = reportType === 'orders'
    ? (isArabic ? 'تقرير الطلبات' : 'Orders Report')
    : (isArabic ? 'تقرير الإيرادات' : 'Revenue Report');

  const doc = new jsPDF({
    orientation: 'landscape',
    format: 'a4'
  });

  // إعداد الخط والاتجاه حسب اللغة
  if (isArabic) {
    addArabicFont(doc);
    doc.setFont('Amiri', 'normal');
  } else {
    doc.setFont('helvetica', 'normal');
  }

  doc.setFontSize(14);
  doc.setTextColor(40);

  doc.text(title, 140, 20, { align: 'center' });

  const headers = isArabic
    ? ['رقم الطلب', 'التاريخ', 'الحالة', 'المبلغ', 'رسوم التوصيل', 'طريقة الدفع']
    : ['Order ID', 'Date', 'Status', 'Amount', 'Delivery Fee', 'Payment Method'];

  const rows = filteredOrders.map(o => [
    o.id,
    o.dateFormatted,
    isArabic
      ? (o.status === 'completed' ? t('status_delivered') :
         o.status === 'cancelled' ? t('status_cancelled') : t(o.status))
      : (o.status === 'completed' ? 'Delivered' :
         o.status === 'cancelled' ? 'Cancelled' : o.status),
    o.total.toFixed(3),
    o.deliveryFee.toFixed(3),
    o.paymentMethod
  ]);

  autoTable(doc, {
    head: [headers],
    body: rows,
    styles: {
      font: isArabic ? 'Amiri' : 'helvetica',
      fontStyle: 'normal',
      fontSize: 10,
      halign: isArabic ? 'right' : 'left',
      cellPadding: 3
    },
    headStyles: {
      fillColor: [40, 60, 90],
      textColor: 255,
      fontSize: 12,
      halign: isArabic ? 'right' : 'left'
    },
    margin: { top: 30 },
    theme: 'grid'
  });

  doc.save(`${title.replace(/ /g, '_')}.pdf`);
};

  const COLORS = ['#0088FE','#00C49F','#FFBB28','#FF8042','#8884d8','#FF6B6B','#4CAF50'];

  if (loading) {
    return <LoadingSpinner text={t('loading')} />;
  }

  const formatCurrency = amt => `${amt.toFixed(3)} د.ك`;

  return (
    <div className="admin-reports-page" dir="rtl">
      <AdminSidebar />
      <div className="admin-reports-content">
        <AdminHeader title={t('reports')} />
        <div className="reports-filters">
          <select value={dateRange} onChange={e=>setDateRange(e.target.value)}>
            <option value="day">{t('today')}</option>
            <option value="week">{t('last_week')}</option>
            <option value="month">{t('last_month')}</option>
            <option value="year">{t('last_year')}</option>
          </select>
          <select value={reportType} onChange={e=>setReportType(e.target.value)}>
            <option value="orders">{t('orders_report')}</option>
            <option value="revenue">{t('revenue_report')}</option>
          </select>
          <button onClick={exportToExcel} className="export-btn">📥 {t('export_excel')}</button>
          <button onClick={exportToPDF} className="export-btn">📄 {t('export_pdf')}</button>
        </div>

            <div className="stats-grid">
              <div className="stat-card completed-stat-card" onClick={() => setShowCompletedSection(!showCompletedSection)}>
                <h3>{t('orders_completed')}</h3>
                <p>{stats.completedOrders}</p>
                <small>{stats.completedOrders > 0 ? Math.round((stats.completedOrders / stats.totalOrders) * 100) : 0}% من إجمالي الطلبات</small>
              </div>
              <div className="stat-card">
                <h3>{t('total_orders')}</h3>
                <p>{filteredOrders.length}</p>
                <small className="period-note">للفترة المحددة</small>
              </div>
              <div className="stat-card">
                <h3>{t('cancelled_orders')}</h3>
                <p>{filteredOrders.filter(order => order.status === 'cancelled').length}</p>
                <small>{filteredOrders.filter(order => order.status === 'cancelled').length > 0 ? Math.round((filteredOrders.filter(order => order.status === 'cancelled').length / filteredOrders.length) * 100) : 0}% من الطلبات</small>
              </div>
              <div className="stat-card">
                <h3>{t('total_revenue')}</h3>
                <p>{formatCurrency(filteredOrders.reduce((sum, order) => sum + (order.status === 'completed' ? (order.total || 0) : 0), 0))}</p>
                <small className="period-note">للفترة المحددة</small>
              </div>
              <div className="stat-card">
                <h3>{t('delivery_fees')}</h3>
                <p>{formatCurrency(filteredOrders.reduce((sum, order) => sum + (order.deliveryFee || 0), 0))}</p>
              </div>
              <div className="stat-card">
                <h3>{t('average_order_value')}</h3>
                <p>{formatCurrency(
                  filteredOrders.length > 0 ? 
                  filteredOrders.reduce((sum, order) => sum + (order.total || 0), 0) / filteredOrders.length : 
                  0
                )}</p>
              </div>
              <div className="stat-card">
                <h3>{t('average_delivery_time')}</h3>
                <p>{stats.avgDeliveryTime} دقيقة</p>
              </div>
              <div className="stat-card">
                <h3>{t('total_drivers')}</h3>
                <p>{stats.totalDrivers}</p>
              </div>
              <div className="stat-card">
                <h3>{t('total_restaurants')}</h3>
                <p>{stats.totalRestaurants}</p>
              </div>
            </div>

            {/* قسم إحصائيات الطلبات المكتملة */}
            {showCompletedSection && (
              <div className="completed-orders-section">
                <h2>{t('completed_orders_statistics')}</h2>
                <div className="completed-stats">
                  <div className="completed-stat-card">
                    <h3>{t('total_completed_orders')}</h3>
                    <p>{completedOrdersData.length}</p>
                    <small className="period-note">للفترة المحددة</small>
                  </div>
                  <div className="completed-stat-card">
                    <h3>{t('total_completed_revenue')}</h3>
                    <p>{formatCurrency(completedOrdersData.reduce((sum, order) => sum + (order.total || 0), 0))}</p>
                  </div>
                  <div className="completed-stat-card">
                    <h3>{t('completion_percentage')}</h3>
                    <p>{filteredOrders.length > 0 ? Math.round((completedOrdersData.length / filteredOrders.length) * 100) : 0}%</p>
                  </div>
                </div>
                
                <div className="charts-container">
                  {/* أفضل المطاعم من حيث الطلبات المكتملة */}
                  <div className="chart-card">
                    <h3>{t('best_restaurants_by_completed_orders')}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={completedByRestaurantData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={120} />
                        <Tooltip formatter={(value, name) => name === 'count' ? `${value} طلب` : formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="count" name={t('orders')} fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* أفضل السائقين من حيث الطلبات المكتملة */}
                  <div className="chart-card">
                    <h3>{t('best_drivers_by_completed_orders')}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={completedByDriverData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={120} />
                        <Tooltip formatter={(value, name) => name === 'count' ? `${value} طلب` : formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="count" name={t('orders')} fill="#00C49F" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* جدول الطلبات المكتملة (أحدث 10 طلبات) */}
                <div className="completed-orders-table">
                  <h3>{t('latest_completed_orders')}</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>{t('order_number')}</th>
                        <th>{t('restaurant')}</th>
                        <th>{t('driver')}</th>
                        <th>{t('customer')}</th>
                        <th>{t('order_date')}</th>
                        <th>{t('orderAmount')}</th>
                        <th>{t('delivery_fee')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedOrdersData.slice(0, 10).map(order => (
                        <tr key={order.id}>
                          <td>{order.id.substring(0, 8)}...</td>
                          <td>{order.restaurantName || t('unknown_restaurant')}</td>
                          <td>{order.driverName || t('unknown_driver')}</td>
                          <td>{order.customerName || t('unknown_customer')}</td>
                          <td>{order.dateFormatted}</td>
                          <td>{formatCurrency(order.total || 0)}</td>
                          <td>{formatCurrency(order.deliveryFee || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="charts-container">
              <div className="chart-card">
                <h3>{reportType === 'orders' ? t('orders_over_time') : t('revenue_over_time')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData} dir="rtl">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey={reportType === 'orders' ? 'orders' : 'revenue'}
                      stroke="#8884d8"
                      activeDot={{ r: 8 }}
                      name={reportType === 'orders' ? t('orders') : t('revenue')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-card">
                <h3>{t('payment_methods')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={paymentMethodData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {paymentMethodData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-card">
                <h3>{t('order_statuses')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={orderStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {orderStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
      </div>
    </div>
  );
};

export default AdminReports;